import { CandidateViewModel } from "../../model/CandidateViewModel";
import { CandidateCourseViewModel } from "../../model/courses/CandidateCourseViewModel";
import { DocumentReferenceViewModel } from "../../model/DocumentReferenceViewModel";
import { ExamSessionViewModel } from "../../model/ExamSessionViewModel";
import { ExpertMeViewModel } from "../../model/ExpertMeViewModel";
import { CandidateMyModuleViewModel } from "../../model/me/CandidateMyModuleViewModel";
import { UploadDocumentsTaskDetailsViewModel } from "../../model/personTask/UploadDocumentsTaskDetailsViewModel";
import { PersonTaskIndex } from "../../model/PersonTaskIndex";
import { ApiResponse } from "../CustomFetch";
import { api, transformApiResponse } from "./BaseApi";

export const meUrl = "Me";
const meAsExpertUrl = `${meUrl}/Expert`;

const meApi = api
  .enhanceEndpoints({
    addTagTypes: [
      "Me",
      "MeAsExpert",
      "MyCandidateExams",
      "MyModuleDocuments",
      "MyCourses",
      "MyExamSessions",
      "MyTasks",
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getMe: builder.query<CandidateViewModel, void>({
        query: () => meUrl,
        transformResponse: transformApiResponse,
        providesTags: ["Me"],
      }),

      getMeAsExpert: builder.query<ExpertMeViewModel, void>({
        query: () => meAsExpertUrl,
        transformResponse: transformApiResponse,
        providesTags: ["MeAsExpert"],
      }),

      getMyModulesAndExamsForCandidate: builder.query<CandidateMyModuleViewModel[], void>({
        query: () => `${meUrl}/candidateModulesAndExams`,
        transformResponse: transformApiResponse,
        providesTags: ["MyCandidateExams"],
      }),

      getMyExamSessions: builder.query<ExamSessionViewModel[], void>({
        query: () => `${meUrl}/examSessions`,
        transformResponse: transformApiResponse,
        providesTags: ["MyExamSessions"],
      }),

      getMyTasks: builder.query<PersonTaskIndex[], void>({
        query: () => `${meUrl}/tasks`,
        transformResponse: transformApiResponse,
        providesTags: ["MyTasks"],
      }),

      updateMe: builder.mutation<ApiResponse<void>, CandidateViewModel>({
        query: (candidate: CandidateViewModel) => ({
          url: meUrl,
          body: candidate,
          method: "PUT",
        }),
        invalidatesTags: ["Me"],
      }),

      updateMeAsExpert: builder.mutation<ApiResponse<void>, ExpertMeViewModel>({
        query: (expert: ExpertMeViewModel) => ({
          url: meAsExpertUrl,
          body: expert,
          method: "PUT",
        }),
        invalidatesTags: ["MeAsExpert"],
      }),

      getUploadDocumentsTaskDetails: builder.query<
        UploadDocumentsTaskDetailsViewModel,
        { taskId: string; examSessionId: string; moduleId: string }
      >({
        query: ({ taskId, examSessionId, moduleId }) =>
          `${meUrl}/task/uploadDocuments/${taskId}?moduleId=${moduleId}&examSessionId=${examSessionId}`,
        transformResponse: transformApiResponse,
      }),

      completeUploadDocumentsTask: builder.mutation<
        ApiResponse<void>,
        { taskId: string; moduleId: string; examSessionId: string }
      >({
        query: ({ taskId, moduleId, examSessionId }) => ({
          url: `${meUrl}/task/uploadDocuments/${taskId}/complete?moduleId=${moduleId}&examSessionId=${examSessionId}`,
          method: "POST",
        }),
      }),

      listMyModuleDocuments: builder.query<DocumentReferenceViewModel[], { moduleId: string }>({
        query: ({ moduleId }) => `${meUrl}/moduleDocuments/${moduleId}`,
        transformResponse: transformApiResponse,
        providesTags: (_result, _error, { moduleId }) => [{ type: "MyModuleDocuments", id: moduleId }],
      }),

      addMyModuleDocument: builder.mutation<ApiResponse<void>, { moduleId: string; file: File }>({
        query: ({ moduleId, file }) => {
          const formData = new FormData();
          formData.append("file", file);
          return {
            url: `${meUrl}/moduleDocuments/${moduleId}`,
            body: formData,
            method: "POST",
          };
        },
        invalidatesTags: (_result, _error, { moduleId }) => [{ type: "MyModuleDocuments", id: moduleId }],
      }),

      deleteMyModuleDocument: builder.mutation<ApiResponse<void>, { moduleId: string; fileName: string }>({
        query: ({ moduleId, fileName }) => ({
          url: `${meUrl}/moduleDocuments/${moduleId}/${fileName}`,
          method: "DELETE",
        }),
        invalidatesTags: (_result, _error, { moduleId }) => [{ type: "MyModuleDocuments", id: moduleId }],
      }),
      
      getCandidateCourses: builder.query<CandidateCourseViewModel[], void>({
        query: () => `${meUrl}/candidateCourse`,
        transformResponse: transformApiResponse,
        providesTags: ["MyCourses"],
      }),
    }),
  });

export const {
  endpoints,
  useGetMeQuery,
  useGetMeAsExpertQuery,
  useUpdateMeMutation,
  useUpdateMeAsExpertMutation,
  useGetMyModulesAndExamsForCandidateQuery,
  useGetMyExamSessionsQuery,
  useGetMyTasksQuery,
  useGetUploadDocumentsTaskDetailsQuery,
  useCompleteUploadDocumentsTaskMutation,
  useListMyModuleDocumentsQuery,
  useAddMyModuleDocumentMutation,
  useDeleteMyModuleDocumentMutation,
  useGetCandidateCoursesQuery,
} = meApi;
